import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Container,
  Divider,
  Flex,
  Group,
  Loader,
  Paper,
  Stack,
  Switch,
  Text,
  Title,
  Tooltip,
  Center,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBell, IconLock } from '@tabler/icons-react';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Practitioner, Patient } from '@medplum/fhirtypes';
import { usePermissions } from '@/contexts/PermissionContext';
import type { ProfileRoleCode } from '@unify/fhir-model/constants';
import { fhirPutResource } from '@/pages/appointments/utils/fhirWrite';
import { LocalhostOnlyBadge } from '@/components/LocalhostOnlyBadge';
import {
  type NotificationPreferencesConfig,
  type NotificationPreferenceCategory,
  type ChannelStatus,
  CATEGORY_META,
  CHANNEL_AVAILABILITY,
  getPreferenceGroupsForRole,
  getDefaultPreferencesForRole,
  mergeWithDefaults,
  validatePreferences,
  readNotificationPreferences,
  writeNotificationPreferences,
} from '@/lib/notificationPreferences';

type ProfileResource = Practitioner | Patient;

const STATUS_TOOLTIP: Record<ChannelStatus, string> = {
  active: '',
  'locked-on': 'Always enabled',
  'coming-soon': 'Coming soon',
};

function ChannelToggle({
  status,
  checked,
  locked,
  onChange,
  ariaLabel,
}: {
  status: ChannelStatus;
  checked: boolean;
  locked?: boolean;
  onChange: (value: boolean) => void;
  ariaLabel: string;
}): JSX.Element {
  const isDisabled = locked || status !== 'active';
  const isChecked = status === 'locked-on' ? true : status === 'coming-soon' ? false : checked;
  const tooltip = STATUS_TOOLTIP[status];

  const toggle = (
    <div style={{ width: 60, display: 'flex', justifyContent: 'center' }}>
      <Switch
        size="sm"
        color="gold"
        checked={isChecked}
        disabled={isDisabled}
        onChange={(e) => onChange(e.currentTarget.checked)}
        aria-label={ariaLabel}
      />
    </div>
  );

  if (tooltip) {
    return <Tooltip label={tooltip} withArrow>{toggle}</Tooltip>;
  }
  return toggle;
}

export function NotificationPreferencesSettings(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as ProfileResource | undefined;
  const rbac = usePermissions();
  const role = rbac.role as ProfileRoleCode;

  const [config, setConfig] = useState<NotificationPreferencesConfig | null>(null);
  const [savedConfig, setSavedConfig] = useState<NotificationPreferencesConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile || !rbac.ready) return;
    try {
      const stored = readNotificationPreferences(profile);
      const merged = mergeWithDefaults(stored, role);
      setConfig(merged);
      setSavedConfig(merged);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  }, [profile, role, rbac.ready]);

  const isDirty = useMemo(() => {
    if (!config || !savedConfig) return false;
    return JSON.stringify(config.settings) !== JSON.stringify(savedConfig.settings);
  }, [config, savedConfig]);

  const groups = useMemo(() => getPreferenceGroupsForRole(role), [role]);

  const toggleChannel = useCallback(
    (category: NotificationPreferenceCategory, channel: 'inApp' | 'email', value: boolean) => {
      setConfig((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          settings: prev.settings.map((s) =>
            s.category === category
              ? { ...s, channels: { ...s.channels, [channel]: value } }
              : s,
          ),
        };
      });
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!config || !profile) return;
    const validation = validatePreferences(config);
    if (!validation.valid) {
      notifications.show({
        title: 'Validation error',
        message: validation.errors.join('; '),
        color: 'red',
      });
      return;
    }

    setSaving(true);
    try {
      const now = new Date().toISOString();
      const profileRef = `${profile.resourceType}/${profile.id}`;
      const toSave: NotificationPreferencesConfig = {
        ...config,
        updatedAt: now,
        updatedBy: profileRef,
      };
      const updated = writeNotificationPreferences(profile, toSave) as ProfileResource & { id: string };
      await fhirPutResource(medplum, updated);
      setConfig(toSave);
      setSavedConfig(toSave);
      notifications.show({
        title: 'Saved',
        message: 'Notification preferences updated.',
        color: 'green',
      });
    } catch (e) {
      notifications.show({
        title: 'Save failed',
        message: e instanceof Error ? e.message : 'Could not save preferences.',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }, [config, profile, medplum]);

  const handleReset = useCallback(() => {
    const defaults = getDefaultPreferencesForRole(role);
    setConfig(defaults);
    notifications.show({
      title: 'Defaults restored',
      message: 'Click "Save preferences" to apply the default settings.',
      color: 'blue',
      autoClose: 5000,
    });
  }, [role]);

  if (loading || !rbac.ready) {
    return (
      <Container size="lg" py="xl">
        <Center py="xl"><Loader size="lg" /></Center>
      </Container>
    );
  }

  if (error || !config) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Could not load notification preferences">
          {error ?? 'No profile available.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py={{ base: 'md', sm: 'xl' }} px={{ base: 'md', sm: 0 }}>
      <Stack gap="xl">
        <div>
          <Group gap="xs" align="center" mb="xs">
            <Title order={2}>Notifications</Title>
            <LocalhostOnlyBadge />
          </Group>
          <Text size="sm" c="dimmed">Manage how you receive notifications.</Text>
        </div>

        <Paper p="lg" withBorder>
          <Group gap="md" wrap="wrap" align="flex-start">
            <IconBell size={24} stroke={1.5} style={{ color: 'var(--mantine-color-gray-6)', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} size="sm" mb={4}>In-app notifications</Text>
              <Text size="sm" c="dimmed">
                Notifications appear in the bell icon in the top navigation bar.
                New notifications are indicated by a red dot and highlighted in blue.
                Click a notification to mark it as read and navigate to the related page.
              </Text>
            </div>
          </Group>
        </Paper>

        {groups.map((group) => (
          <Paper key={group.id} p="lg" withBorder>
            <Stack gap="sm">
              <Title order={5}>{group.label}</Title>
              <Divider />
              <Group justify="flex-end" gap="xl" pr={4} visibleFrom="sm">
                <Text size="xs" fw={600} c="dimmed" w={60} ta="center">In-app</Text>
                <Text size="xs" fw={600} c="dimmed" w={60} ta="center">Email</Text>
                <Text size="xs" fw={600} c="dimmed" w={60} ta="center">Text</Text>
              </Group>
              {group.categories.map((cat) => {
                const setting = config.settings.find((s) => s.category === cat);
                if (!setting) return null;
                const meta = CATEGORY_META[cat];
                return (
                  <Flex
                    key={cat}
                    direction={{ base: 'column', sm: 'row' }}
                    justify="space-between"
                    align={{ base: 'stretch', sm: 'center' }}
                    gap="sm"
                    py={4}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Group gap={6} wrap="wrap">
                        <Text size="sm" fw={500}>{meta.label}</Text>
                        {setting.locked && (
                          <Tooltip label={setting.reason ?? 'Locked'} withArrow>
                            <Badge size="xs" variant="light" color="gray" leftSection={<IconLock size={10} />}>
                              Required
                            </Badge>
                          </Tooltip>
                        )}
                      </Group>
                      {meta.description && (
                        <Text size="xs" c="dimmed">{meta.description}</Text>
                      )}
                    </div>
                    <Group gap="xl" wrap="wrap" justify="flex-start" style={{ flexShrink: 0 }}>
                      <Stack gap={2} align="center">
                        <Text size="xs" fw={600} c="dimmed" hiddenFrom="sm">In-app</Text>
                        <ChannelToggle
                          status={CHANNEL_AVAILABILITY[cat]?.inApp ?? 'active'}
                          checked={setting.channels.inApp}
                          locked={setting.locked}
                          onChange={(v) => toggleChannel(cat, 'inApp', v)}
                          ariaLabel={`${meta.label} in-app`}
                        />
                      </Stack>
                      <Stack gap={2} align="center">
                        <Text size="xs" fw={600} c="dimmed" hiddenFrom="sm">Email</Text>
                        <ChannelToggle
                          status={CHANNEL_AVAILABILITY[cat]?.email ?? 'active'}
                          checked={setting.channels.email}
                          locked={setting.locked}
                          onChange={(v) => toggleChannel(cat, 'email', v)}
                          ariaLabel={`${meta.label} email`}
                        />
                      </Stack>
                      <Stack gap={2} align="center">
                        <Text size="xs" fw={600} c="dimmed" hiddenFrom="sm">Text</Text>
                        <Tooltip label="Text messaging coming soon" withArrow>
                          <div style={{ width: 60, display: 'flex', justifyContent: 'center' }}>
                            <Switch
                              size="sm"
                              checked={false}
                              disabled
                              aria-label={`${meta.label} text`}
                            />
                          </div>
                        </Tooltip>
                      </Stack>
                    </Group>
                  </Flex>
                );
              })}
            </Stack>
          </Paper>
        ))}

        <Group justify="flex-end" gap="sm" wrap="wrap">
          <Button variant="outline" color="gray" onClick={handleReset} disabled={saving} w={{ base: '100%', sm: 'auto' }}>
            Reset to defaults
          </Button>
          <Button color="gold" onClick={handleSave} loading={saving} disabled={!isDirty} w={{ base: '100%', sm: 'auto' }}>
            Save preferences
          </Button>
        </Group>
      </Stack>
    </Container>
  );
}
